import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import './ProjectSetupWizard.css';

const ProjectSetupWizard = ({ onComplete, onCancel }) => {
  const { projects, createProject, userPlan } = useProject();
  const [url, setUrl] = useState('');
  const [projectName, setProjectName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Determine if this is the first project or adding a new one
  const isFirstProject = projects.length === 0;

  // Validate and normalize URL to base domain only
  const validateAndNormalizeUrl = (inputUrl) => {
    let url = inputUrl.trim();
    
    // Add protocol if missing
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    try {
      const urlObj = new URL(url);
      const origin = urlObj.origin; // This gives us protocol + hostname + port
      const hasPath = urlObj.pathname && urlObj.pathname !== '/' && urlObj.pathname !== '';
      const hasQuery = urlObj.search && urlObj.search !== '';
      const hasFragment = urlObj.hash && urlObj.hash !== '';

      // Check if user entered a page URL instead of domain
      if (hasPath || hasQuery || hasFragment) {
        return {
          valid: false,
          error: 'Please enter only the website domain (e.g., example.com). Do not include page paths, slugs, or query parameters.',
          normalizedUrl: origin // Return the base domain anyway
        };
      }

      // Validate domain format (must have at least one dot for TLD)
      const hostname = urlObj.hostname;
      if (!hostname.includes('.')) {
        return {
          valid: false,
          error: 'Please enter a valid domain name (e.g., example.com)',
          normalizedUrl: null
        };
      }

      // Check for localhost or IP addresses (optional - you might want to allow these)
      if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return {
          valid: false,
          error: 'Please enter a valid website domain, not localhost or IP address',
          normalizedUrl: null
        };
      }

      return {
        valid: true,
        error: null,
        normalizedUrl: origin
      };
    } catch (error) {
      return {
        valid: false,
        error: 'Please enter a valid website domain (e.g., example.com)',
        normalizedUrl: null
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    if (!projectName.trim()) {
      setError('Please enter a project name');
      return;
    }

    // Check project limit based on plan (matches DashboardLayout logic)
    // This check is also done in DashboardLayout, but keeping it here as a safety check
    // Backend will also validate, but frontend check provides better UX
    const normalizedPlan = (userPlan || 'basic').toLowerCase();
    const planLimits = {
      'basic': 1,
      'individual': 1,
      'business': 10,
      'enterprise': 50
    };
    
    // Lifetime plan has unlimited projects, skip check
    if (normalizedPlan !== 'lifetime' && normalizedPlan !== 'custom') {
      const projectLimit = planLimits[normalizedPlan] || 1;
      if (projects.length >= projectLimit) {
        if (normalizedPlan === 'basic' || normalizedPlan === 'individual') {
          setError(`${normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1)} plan allows only ${projectLimit} project${projectLimit > 1 ? 's' : ''}. Please upgrade to add more projects.`);
        } else {
          setError(`You have reached your project limit of ${projectLimit}. Please upgrade to add more projects.`);
        }
        return;
      }
    }

    // Validate and normalize URL to base domain only
    const urlValidation = validateAndNormalizeUrl(url);
    if (!urlValidation.valid) {
      setError(urlValidation.error);
      return;
    }

    const targetUrl = urlValidation.normalizedUrl;

    setLoading(true);

    try {
      // Create project via API (which will check limits and save to database)
      const project = await createProject({
        name: projectName.trim(),
        url: targetUrl,
      });

      setLoading(false);
      if (onComplete) {
        onComplete(project);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Failed to create project. Please try again.');
    }
  };


  return (
    <div className="wizard-container">
      <div className="wizard-layout">
        {/* Left Column - Image */}
        <div className="wizard-image-column">
          <img 
            src="https://emdlfhtbfpdzhowvzssr.supabase.co/storage/v1/object/public/Public/ParrotSEO%20Wizard.webp" 
            alt="Parrot SEO" 
            className="wizard-image"
          />
        </div>

        {/* Right Column - Wizard Form */}
        <div className="wizard-form-column">
          <div className="wizard-form-wrapper">
            <div className="wizard-header">
              <h1 className="wizard-title">
                {isFirstProject ? 'Set Up Your First Project' : 'Create New Project'}
              </h1>
              <p className="wizard-subtitle">
                {isFirstProject 
                  ? 'Create a project to start analyzing your website\'s SEO performance'
                  : 'Add a new project to track and analyze multiple websites'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="wizard-form">
              {error && <div className="wizard-error">{error}</div>}

              <div className="form-group">
                <label htmlFor="projectName">Project Name</label>
                <input
                  type="text"
                  id="projectName"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                  placeholder="e.g., My Website"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="url">Website Domain</label>
                <input
                  type="text"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  placeholder="example.com"
                  disabled={loading}
                />
                <small className="form-hint">Enter only the website domain (e.g., example.com). Do not include page paths or slugs.</small>
              </div>

              <div className="wizard-actions">
                {!isFirstProject && onCancel && (
                  <button 
                    type="button" 
                    className="wizard-button-back" 
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Back
                  </button>
                )}
                <button type="submit" className="wizard-button" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSetupWizard;

